import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config/config";

// /offer/:token — candidate-facing offer page with native canvas e-signature.
//
// Flow:
//   GET    /job-posting/api/public/offer/{token}            fetch offer + consent text
//   POST   /job-posting/api/public/offer/{token}/sign       submit signature
//   POST   /job-posting/api/public/offer/{token}/decline    decline with reason
//
// Inline canvas signature pad (no react-signature-canvas dep) — accepts
// pointer/touch events, exports PNG via toDataURL().
export default function OfferAcceptance() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState("accept"); // "accept" | "decline"
  const [typedName, setTypedName] = useState("");
  const [consent, setConsent] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(null);
  const [declineReason, setDeclineReason] = useState("");

  const canvasRef = useRef(null);
  const drawingRef = useRef({ drawing: false, x: 0, y: 0 });

  useEffect(() => {
    document.title = "Your offer - Ample Hire";
    axios
      .get(`${baseUrl}/job-posting/api/public/offer/${token}`)
      .then((r) => setOffer(r.data))
      .catch((e) => {
        const status = e?.response?.status;
        setLoadError(
          status === 410
            ? (e.response.data?.error || "This offer link is no longer valid.")
            : status === 404
            ? "Offer not found."
            : "Couldn't load the offer."
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  // Canvas drawing handlers.
  const getPoint = (e) => {
    const c = canvasRef.current;
    if (!c) return { x: 0, y: 0 };
    const rect = c.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };
  const startDraw = (e) => {
    e.preventDefault();
    const { x, y } = getPoint(e);
    drawingRef.current = { drawing: true, x, y };
  };
  const draw = (e) => {
    if (!drawingRef.current.drawing) return;
    e.preventDefault();
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const { x, y } = getPoint(e);
    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(drawingRef.current.x, drawingRef.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    drawingRef.current.x = x;
    drawingRef.current.y = y;
    if (!hasSignature) setHasSignature(true);
  };
  const endDraw = (e) => {
    e.preventDefault();
    drawingRef.current.drawing = false;
  };
  const clearSignature = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasSignature(false);
  };

  const canSign = typedName.trim().length > 1 && hasSignature && consent && !busy;

  const submitSign = async () => {
    if (!canSign) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const r = await axios.post(`${baseUrl}/job-posting/api/public/offer/${token}/sign`, {
        signatureImageBase64: dataUrl,
        typedName: typedName.trim(),
        consentAcknowledged: true,
        consentTextVersion: offer?.consentVersion,
        userAgent: navigator.userAgent,
      });
      setDone({ ...r.data, mode: "sign" });
    } catch (e) {
      setSubmitError(e?.response?.data?.error || e?.message || "Sign failed");
    } finally {
      setBusy(false);
    }
  };

  const submitDecline = async () => {
    setBusy(true);
    setSubmitError(null);
    try {
      const r = await axios.post(`${baseUrl}/job-posting/api/public/offer/${token}/decline`, {
        reason: declineReason || null,
      });
      setDone({ ...r.data, mode: "decline" });
    } catch (e) {
      setSubmitError(e?.response?.data?.error || e?.message || "Decline failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-slate-500">Loading offer…</div>;
  if (loadError) return <Closed>{loadError}</Closed>;
  if (!offer) return <Closed>Offer not found.</Closed>;

  if (done) {
    const accepted = done.mode === "sign";
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-10 text-center">
          <div className="text-6xl mb-3">{accepted ? "🎉" : "✓"}</div>
          <h2 className="text-2xl font-semibold text-slate-900">
            {accepted ? "Welcome aboard!" : "Decline recorded"}
          </h2>
          <p className="text-sm text-slate-500 mt-2">
            {accepted
              ? "Your signed offer is on file. We'll be in touch shortly with next steps."
              : "Thanks for letting us know. We appreciate the time you spent with us."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-block px-3 py-1 rounded-full bg-vf-tint text-vf-primary text-xs font-medium mb-2">
            You've received an offer!
          </div>
          <h1 className="text-3xl font-semibold text-slate-900">Ample Hire</h1>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-8 mb-5">
          <div
            className="prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: offer.renderedHtml || "<p>No offer content.</p>" }}
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setTab("accept")}
              className={`flex-1 px-4 py-3 text-sm font-medium ${tab === "accept" ? "bg-emerald-50 text-emerald-700 border-b-2 border-emerald-500" : "text-slate-600 hover:bg-slate-50"}`}
            >
              ✓ Accept offer
            </button>
            <button
              onClick={() => setTab("decline")}
              className={`flex-1 px-4 py-3 text-sm font-medium ${tab === "decline" ? "bg-red-50 text-red-700 border-b-2 border-red-500" : "text-slate-600 hover:bg-slate-50"}`}
            >
              ✗ Decline
            </button>
          </div>

          {tab === "accept" && (
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Type your full name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="e.g. Aanya Kumar"
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-slate-600">
                    Signature <span className="text-red-500">*</span>
                  </label>
                  <button onClick={clearSignature} className="text-xs text-slate-500 hover:underline">
                    Clear
                  </button>
                </div>
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={150}
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={startDraw}
                  onTouchMove={draw}
                  onTouchEnd={endDraw}
                  className="w-full bg-slate-50 border border-slate-300 rounded cursor-crosshair touch-none"
                  style={{ touchAction: "none" }}
                />
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded p-3">
                <div className="text-[11px] text-slate-500 leading-relaxed mb-2">
                  {offer.consentText || "Electronic signature consent text not loaded."}
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span className="text-xs text-slate-700">
                    I acknowledge the electronic signature consent and accept this offer.
                  </span>
                </label>
              </div>

              {submitError && (
                <div className="border border-red-200 bg-red-50 rounded p-3 text-xs text-red-700">{submitError}</div>
              )}

              <button
                onClick={submitSign}
                disabled={!canSign}
                className="w-full px-4 py-2.5 rounded bg-emerald-600 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? "Signing…" : "Sign and accept"}
              </button>
              {!canSign && !busy && (
                <div className="text-[11px] text-slate-500 text-center">
                  Type your name, draw your signature, and acknowledge consent to continue.
                </div>
              )}
            </div>
          )}

          {tab === "decline" && (
            <div className="p-5 space-y-3">
              <div className="text-sm text-slate-700">We're sorry to hear that.</div>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                rows={3}
                placeholder="Reason (optional)"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              />
              {submitError && (
                <div className="border border-red-200 bg-red-50 rounded p-3 text-xs text-red-700">{submitError}</div>
              )}
              <button
                onClick={submitDecline}
                disabled={busy}
                className="w-full px-4 py-2.5 rounded bg-red-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                {busy ? "Recording…" : "Confirm decline"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Closed({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-10 text-center">
        <div className="text-3xl mb-3">📭</div>
        <div className="text-sm font-medium text-slate-900">{children}</div>
      </div>
    </div>
  );
}
